/* eslint-disable no-void -- this file is effectively client-side only */

import type { USProvince } from '@/constructs/provinces/US/USProvince';
import type { IAddress } from '@/services/models/Address';
import { CartModel, type ICart } from '@/services/models/Cart';
import { MoneyModel } from '@/services/models/Money';
import {
  OrderDigitalShippingInfoModel,
  OrderPhysicalShippingInfoModel,
  type OrderShippingInfoModel,
  type PlacedOrderModel
} from '@/services/models/Order';
import type { ShippingMethodModel } from '@/services/models/ShippingMethod';
import { previewShippingCostForMethod } from '@/services/models/ShippingMethod/utils/previewShippingCostForMethod';
import { ApplePayValidateMerchantService } from '@/services/serverless/integrations/ApplePayValidateMerchantService';
import { isFullNameDOMSCompatible } from '@/services/utils/order-validations/isFullNameDOMSCompatible';
import { difference, pickMap } from '@/utils/array-utils';
import { withPromiseResolvers } from '@/utils/async-utils';
import { invokeImmediately } from '@/utils/function-utils';
import MemoryCache from '@/utils/MemoryCache';
import { isNullOrEmpty, isNullOrZero } from '@/utils/null-utils';
import { isValidEmail } from '@/utils/string-utils';
import {
  CouponRejectionReason,
  RejectedCouponError
} from '@/services/models/Cart/Coupon';
import type { ApplePayPaymentBundle } from '.';
import { Config } from '../ConfigurationService';
import ConfigurationService from '../ConfigurationService/ConfigurationService';
import { EnvironmentService } from '../EnvironmentService';
import I18NService, { msg, msgf, type Country } from '../I18NService';
import LoggerService from '../LoggerService';
import PromotionsService from '../PromotionsService';
import {
  PaymentAbortedError,
  PaymentCanceledError,
  PaymentTimeoutError
} from './errors';
import {
  IPaymentMethodService,
  OnPaymentAuthorizedCallback,
  type IExpressPayment
} from './IPaymentMethodService';
import { order_orderSummary_subTotal } from "@/lang/__generated__/ahnu/order_orderSummary_subTotal";
import { order_orderSummary_discount } from "@/lang/__generated__/ahnu/order_orderSummary_discount";
import { order_orderSummary_shipping } from "@/lang/__generated__/ahnu/order_orderSummary_shipping";
import { order_orderSummary_tax } from "@/lang/__generated__/ahnu/order_orderSummary_tax";
import { order_orderSummary_giftCard } from "@/lang/__generated__/ahnu/order_orderSummary_giftCard";
import { checkout_shipping_errors_cannotShipInternationally } from "@/lang/__generated__/ahnu/checkout_shipping_errors_cannotShipInternationally";
import { checkout_shipping_errors_zipCode } from "@/lang/__generated__/ahnu/checkout_shipping_errors_zipCode";
import { cart_couponCodes_rejection_generic } from "@/lang/__generated__/ahnu/cart_couponCodes_rejection_generic";
import { checkout_shipping_errors_nameTooLong } from "@/lang/__generated__/ahnu/checkout_shipping_errors_nameTooLong";
import { forms_field_email } from "@/lang/__generated__/ahnu/forms_field_email";
import { forms_field_phone } from "@/lang/__generated__/ahnu/forms_field_phone";
import { forms_error_validEmail } from "@/lang/__generated__/ahnu/forms_error_validEmail";
import { forms_error_validPhoneNumber } from "@/lang/__generated__/ahnu/forms_error_validPhoneNumber";

/**
 * Service to handle Apple Pay payment requests.
 * This service is used to process the payment request and retrieve a valid token.
 */
export class ApplePayService implements IPaymentMethodService {
  /**
   * The configuration for Apple Pay.
   * @returns The configuration for Apple Pay.
   */
  public get config(): Config<'applePay'> {
    return ConfigurationService.getConfig('applePay');
  }

  /**
   * Transforms the shipping contact information to an address.
   *
   * **NOTE**:
   *
   * Before the payment is authorized by the user, ApplePay only provides
   * a subset of the shipping contact information, for privacy reasons.
   * However, it should be enough to determine the new applicable shipping
   * methods, and to calculate the new total. Only once the payment is
   * authorized does ApplePay provide the full shipping contact information.
   *
   * This is why we only return a partial address here.
   *
   * @param shippingContact - The shipping contact information.
   * @returns The shipping contact information as an address.
   */
  private transformShippingContact(
    shippingContact: ApplePayJS.ApplePayPaymentContact
  ): Partial<IAddress> {
    return {
      firstName: shippingContact.givenName,
      lastName: shippingContact.familyName,
      addressLine1: shippingContact.addressLines?.[0],
      addressLine2: shippingContact.addressLines?.[1],
      city: shippingContact.locality,
      stateProvince: shippingContact.administrativeArea as USProvince,
      zipPostalCode: shippingContact.postalCode,
      country: shippingContact.countryCode?.toUpperCase() as Country
    };
  }

  /**
   * Transforms the cart totals to a list of Apple Pay line items.
   * @param cart - The cart to transform the totals from.
   * @param isPending - Whether the payment is pending or not.
   * @returns The cart totals as line items.
   */
  private transformTotalToLineItem(
    { total, giftCardAmount }: CartModel,
    isPending: boolean = false
  ): ApplePayJS.ApplePayLineItem {
    return {
      type: isPending ? 'pending' : 'final',
      /**
       * For the total, the label should be a business name, preferably
       * one that matches the charge on the bank statement.
       */
      label: this.config.getSetting('displayName').value,
      amount: total.value.copy().subtractAmount(giftCardAmount).toFixed().amount
    };
  }

  /**
   * Extracts the cart totals as line items.
   * @param cart - The cart to extract the totals from.
   * @returns The cart totals as line items.
   */
  private extractTotalsAsLineItems(
    cart: CartModel
  ): Array<ApplePayJS.ApplePayLineItem> {
    const result: Array<ApplePayJS.ApplePayLineItem> = [];

    const cartSubtotal = cart.subtotal.copy();
    const cartDiscount = cart.discount.value.copy();
    const cartTax = cart.tax.value.copy();
    const cartGiftCardAmount = cart.giftCardAmount.copy();
    const effectiveShippingCost = cart.shippingCost.value.copy();

    // Subtotal
    result.push({
      type: 'final',
      label: msg(order_orderSummary_subTotal),
      amount: cartSubtotal.toFixed().amount
    });

    // Discount (cart-level only; shipping promos are reflected in shipping line + picker)
    if (cartDiscount.isPositive) {
      result.push({
        type: 'final',
        label: msg(order_orderSummary_discount),
        amount: cartDiscount.negate().toFixed().amount
      });
    }

    // Shipping (net after shipping-target promotions)
    result.push({
      type: 'final',
      label: msg(order_orderSummary_shipping),
      amount: effectiveShippingCost.toFixed().amount
    });

    // Tax
    result.push({
      type: 'final',
      label: msg(order_orderSummary_tax),
      amount: cartTax.toFixed().amount
    });

    // Gift Card
    if (cartGiftCardAmount.isPositive) {
      result.push({
        type: 'final',
        label: msg(order_orderSummary_giftCard),
        amount: cartGiftCardAmount.negate().toFixed().amount
      });
    }

    return result;
  }

  /**
   * Net shipping for a row via {@link previewShippingCostForMethod}.
   * Falls back to `fallbackRawCost` when preview returns null.
   *
   * @param sessionCart - Apple Pay session cart.
   * @param previewCache - Per-session preview cache by method uid.
   * @param methodUid - Shipping method uid.
   * @param fallbackRawCost - List price when preview is unavailable.
   * @returns Amount string for the Apple Pay shipping row.
   */
  private async resolveApplePayShippingMethodDisplayAmount(
    sessionCart: CartModel,
    previewCache: MemoryCache<ICart>,
    methodUid: string,
    fallbackRawCost: MoneyModel
  ): Promise<string> {
    // If this is the cart's current selection, we already have the net shipping cost
    // (including shipping-target promos) on the session cart. Avoid spinning up a preview cart.
    if (sessionCart.selectedShippingMethod?.uid === methodUid) {
      return sessionCart.shippingCost.value.copy().toFixed().amount;
    }

    const preview = await previewShippingCostForMethod(
      sessionCart,
      methodUid,
      previewCache
    );

    return (preview ?? fallbackRawCost).toFixed().amount;
  }

  /**
   * One shipping row; amount from {@link previewShippingCostForMethod} when possible.
   *
   * @param sessionCart - Apple Pay session cart.
   * @param previewCache - Shared preview cache for the session.
   * @param shippingMethod - Method to render.
   * @returns Apple Pay shipping method payload.
   */
  private async buildApplePayShippingMethodRow(
    sessionCart: CartModel,
    previewCache: MemoryCache<ICart>,
    shippingMethod: ShippingMethodModel
  ): Promise<ApplePayJS.ApplePayShippingMethod> {
    const {
      uid,
      description,
      name,
      shippingCost,
      earliestDeliveryDays,
      latestDeliveryDays
    } = shippingMethod;

    const amount = await this.resolveApplePayShippingMethodDisplayAmount(
      sessionCart,
      previewCache,
      uid,
      shippingCost
    );

    return {
      identifier: uid,
      detail: description,
      label: name,
      amount,
      dateComponentsRange:
        !isNullOrZero(earliestDeliveryDays) && !isNullOrZero(latestDeliveryDays)
          ? {
              startDateComponents: {
                days: earliestDeliveryDays,
                hours: 0,
                months: 0,
                years: 0
              },
              endDateComponents: {
                days: latestDeliveryDays,
                hours: 0,
                months: 0,
                years: 0
              }
            }
          : undefined
    };
  }

  /**
   * Sorts methods so the cart's {@link CartModel.selectedShippingMethod} is first (Apple Pay
   * preselects index 0), then by increasing raw shipping cost. Always pass the **current**
   * selection after `selectShippingMethod` / address updates so the sheet does not jump back.
   *
   * @param methods - Applicable shipping methods for the cart.
   * @param preferredShippingMethodId - `cart.selectedShippingMethod?.id` to pin first, if any.
   * @returns A new sorted array.
   */
  private sortShippingMethodsForApplePay(
    methods: Array<ShippingMethodModel>,
    preferredShippingMethodId: string | undefined
  ): Array<ShippingMethodModel> {
    const sorted = [...methods];
    sorted.sort((methodA, methodB) => {
      if (methodA.id === preferredShippingMethodId) return -1;
      if (methodB.id === preferredShippingMethodId) return 1;
      return MoneyModel.compare(methodA.shippingCost, methodB.shippingCost);
    });
    return sorted;
  }

  /**
   * Shipping rows for the sheet; each amount uses {@link previewShippingCostForMethod} (checkout
   * uses the same helper through `CartVM.getShippingMethodCost`).
   *
   * @param cart - Cart after shipping-affecting updates.
   * @param previewCache - Session-scoped cache of preview cart DTOs per method uid.
   * @returns Rows for `newShippingMethods` on Apple Pay completion callbacks.
   */
  private async buildApplePayShippingMethodsForSheet(
    cart: CartModel,
    previewCache: MemoryCache<ICart>
  ): Promise<Array<ApplePayJS.ApplePayShippingMethod>> {
    const methods = [...cart.applicableShippingMethods.value];
    const sorted = this.sortShippingMethodsForApplePay(
      methods,
      cart.selectedShippingMethod?.id
    );
    return Promise.all(
      sorted.map(async (method) =>
        this.buildApplePayShippingMethodRow(cart, previewCache, method)
      )
    );
  }

  /**
   * Runs an Apple Pay session until an order is placed.
   * @param cart - The cart to be ordered.
   * @param onPaymentAuthorized - Callback to be called when the payment is authorized.
   * @returns A promise that resolves when the order is placed and
   * the payment sheet is dismissed.
   */
  public async runSession(
    cart: CartModel,
    onPaymentAuthorized: OnPaymentAuthorizedCallback<ApplePayPaymentBundle>
  ): Promise<PlacedOrderModel> {
    const isEnabled = this.config.getSetting('enabled').value;
    if (!isEnabled) {
      throw new Error('Apple Pay is not enabled.');
    }

    if (!window.ApplePaySession) {
      throw new Error('Your browser does not support Apple Pay on the web.');
    }

    const { promise, resolve, reject } =
      withPromiseResolvers<PlacedOrderModel>();

    const applePayVersion = this.config.getSetting('version').value;
    const supportedNetworks = this.config.getSetting('supportedNetworks')
      .value as unknown as Array<string>; // config types are very broken

    const requiredShippingContactFields: Array<ApplePayJS.ApplePayContactField> =
      ['email', 'phone'];

    // gift card carts don't require postal address or name since they are digital only.
    if (cart.type === 'standard') {
      requiredShippingContactFields.push('postalAddress', 'name');
    }

    // lazy preload phone utils to reduce main
    // bundle size until it's likely to be used.
    const phoneUtils = import('@/services/utils/phone-utils').catch((cause) => {
      throw new Error('Failed to load phone validation utilities', { cause });
    });

    const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
      supportedNetworks,
      currencyCode: I18NService.currency,
      countryCode: I18NService.currentLocale.country,
      merchantCapabilities: ['supports3DS'],
      lineItems: this.extractTotalsAsLineItems(cart),
      total:
        cart.type === 'gift-card'
          ? this.transformTotalToLineItem(cart, false)
          : this.transformTotalToLineItem(cart, true),
      requiredShippingContactFields,
      /** `postalAddress` is enough to give us the billing address, including the name. */
      requiredBillingContactFields: ['postalAddress'],
      /**
       * Apple Pay for some reason only supports one coupon code at a time, while we have the
       * ability to support multiple coupon codes. As a result, if a user wants to apply a
       * coupon code, they must do it through our UI before initiating the Apple Pay session.
       */
      supportsCouponCode: false,
      /**
       * Don't initially show the shipping methods, because we need to wait for a
       * shipping contact to be selected before retrieving the applicable shipping methods.
       * Otherwise, the customer will see stale shipping methods.
       */
      shippingMethods: undefined
    };

    const session = new window.ApplePaySession(applePayVersion, paymentRequest);

    /** Per-method preview cart DTOs; passed to {@link previewShippingCostForMethod}. */
    const shippingMethodPreviewCache = new MemoryCache<ICart>();

    // #region Shipping Method Handling
    session.onshippingmethodselected = (event) => {
      const shippingMethodID = event.shippingMethod.identifier;

      void invokeImmediately(async () => {
        try {
          await cart.selectShippingMethod(shippingMethodID);
          const newShippingMethods =
            await this.buildApplePayShippingMethodsForSheet(
              cart,
              shippingMethodPreviewCache
            );
          session.completeShippingMethodSelection({
            newTotal: this.transformTotalToLineItem(cart),
            newLineItems: this.extractTotalsAsLineItems(cart),
            newShippingMethods
          });
        } catch (cause) {
          // `completeShippingMethodSelection` does not support reporting errors.
          // This is because if the shipping method selection fails, then the
          // session must have been in some invalid state, so we should abort it.
          session.abort();
          reject(
            new PaymentAbortedError('Shipping method selection failed.', {
              cause
            })
          );
        }
      });
    };
    // #endregion

    // #region Shipping Contact Handling
    let isFirstShippingContactSelection = true;
    // Note: Apple Pay only fires this for shipping address changes; email
    // and phone number changes do not trigger this event.
    session.onshippingcontactselected = (event) => {
      const { shippingContact } = event;
      const oldApplicableMethods = cart.applicableShippingMethods.value;

      // Business Requirement: fail if the shipping address is outside the current country/locale.
      if (shippingContact.countryCode !== I18NService.currentLocale.country) {
        session.completeShippingContactSelection({
          errors: [
            new ApplePayError(
              'shippingContactInvalid',
              'countryCode',
              msg(checkout_shipping_errors_cannotShipInternationally)
            )
          ],
          newTotal: this.transformTotalToLineItem(cart),
          newLineItems: this.extractTotalsAsLineItems(cart),
          newShippingMethods: []
        });

        return;
      }

      /**
       * **NOTE**: Before payment authorization, ApplePay only provides a subset of the
       * shipping contact information, for privacy reasons. This should be enough to
       * determine the new shipping methods and calculate taxes, but not enough to
       * fully validate the information. Specifically, the only information provided is:
       * - 'countryCode' (country in ISO 3166-1)
       * - 'administrativeArea' (state/province)
       * - 'locality' (city)
       * - 'subLocality' (district/neighborhood/borough)
       * - 'subAdministrativeArea' (county)
       * - 'postalCode' (zip/postal code).
       *
       * As a result, we must skip any other validation until the payment is authorized.
       */

      void invokeImmediately(async () => {
        try {
          const address = this.transformShippingContact(shippingContact);
          // pretend its a full address just so we can recalculate shipping methods and tax.
          await cart.setShipToAddress(address as IAddress);
        } catch (e) {
          LoggerService.error(
            new Error('Shipping contact selection failed.', { cause: e })
          );

          session.completeShippingContactSelection({
            errors: [
              new ApplePayError(
                'shippingContactInvalid',
                'postalCode',
                msg(checkout_shipping_errors_zipCode)
              )
            ],
            newTotal: this.transformTotalToLineItem(cart),
            newLineItems: this.extractTotalsAsLineItems(cart),
            newShippingMethods: []
          });

          return;
        }

        const newApplicableMethods = [...cart.applicableShippingMethods.value];

        const oldApplicableMethodIds = pickMap(oldApplicableMethods, 'uid');
        const newApplicableMethodIds = pickMap(newApplicableMethods, 'uid');

        const [oldIdsDiff, newIdsDiff] = difference(
          oldApplicableMethodIds,
          newApplicableMethodIds
        );

        /**
         * Represents if the shipping methods have changed.
         *
         * This is used to prevent the session from deselecting
         * the previously selected shipping method.
         */
        const hasShippingMethodChanges = !(
          // If both diffs are empty, that means the shipping methods have not changed.
          (oldIdsDiff.length === 0 && newIdsDiff.length === 0)
        );

        session.completeShippingContactSelection({
          newTotal: this.transformTotalToLineItem(cart),
          newLineItems: this.extractTotalsAsLineItems(cart),
          newShippingMethods:
            hasShippingMethodChanges || isFirstShippingContactSelection
              ? await this.buildApplePayShippingMethodsForSheet(
                  cart,
                  shippingMethodPreviewCache
                )
              : undefined
        });

        isFirstShippingContactSelection = false;
      });
    };
    // #endregion

    // #region Coupon Code Handling
    session.oncouponcodechanged = (event) => {
      const { couponCode } = event;

      void invokeImmediately(async () => {
        try {
          await cart.applyCoupon(couponCode);
          await cart.total; // ensure the total is updated

          session.completeCouponCodeChange({
            newTotal: this.transformTotalToLineItem(cart),
            newLineItems: this.extractTotalsAsLineItems(cart)
          });
        } catch (err) {
          let errors: Array<ApplePayJS.ApplePayError> | undefined;

          if (err instanceof RejectedCouponError) {
            errors = [
              new ApplePayError(
                err.rejectionReason === CouponRejectionReason.Expired
                  ? 'couponCodeExpired'
                  : 'couponCodeInvalid',
                undefined,
                PromotionsService.getRejectionReasonMessage(err.rejectionReason)
              )
            ];
          } else {
            errors = [
              new ApplePayError(
                'unknown',
                undefined,
                msg(cart_couponCodes_rejection_generic)
              )
            ];
          }

          session.completeCouponCodeChange({
            errors,
            newTotal: this.transformTotalToLineItem(cart),
            newLineItems: this.extractTotalsAsLineItems(cart)
          });
        }
      });
    };
    // #endregion

    // #region Merchant Validation Handling
    session.onvalidatemerchant = () => {
      /**
       * As soon as the system displays the payment sheet, Apple Pay calls this
       * event handler to verify that the request is coming from a valid merchant.
       *
       * Although the event object provides a `validationURL` property which represents
       * the Apple Pay server endpoint to validate the merchant, this is being deprecated
       * in favor of the static Payment Session endpoint. This also ensures that a customer
       * can't maliciously send a different URL to spoof the Apple Pay server.
       * @see {@link https://developer.apple.com/documentation/apple_pay_on_the_web/apple_pay_js_api/providing_merchant_validation - Providing Merchant Validation}
       */

      void invokeImmediately(async () => {
        try {
          const merchantSession =
            await ApplePayValidateMerchantService.validateWebSession(
              EnvironmentService.url.hostname
            );

          session.completeMerchantValidation(merchantSession);
        } catch (error) {
          // `completeMerchantValidation` does not support reporting errors.
          // This is because merchant validation is a critical step, and so
          // if it fails we must abort the session.
          session.abort();
          reject(
            new PaymentAbortedError('Merchant validation failed.', {
              cause: error
            })
          );
        }
      });
    };
    // #endregion

    // #region Payment Authorization Handling
    const paymentAuthorizationMainHandler = async (
      payment: ApplePayJS.ApplePayPayment
    ): Promise<void> => {
      const { shippingContact, billingContact, token } = payment;

      const errors: Array<ApplePayJS.ApplePayError> = [];

      if (!shippingContact) {
        errors.push(
          new ApplePayError(
            'shippingContactInvalid',
            'emailAddress',
            'Missing shipping contact information.'
          )
        );
      } else if (isNullOrEmpty(shippingContact.emailAddress?.trim())) {
        errors.push(
          new ApplePayError(
            'shippingContactInvalid',
            'emailAddress',
            'Missing shipping contact email address.'
          )
        );
      } else if (isNullOrEmpty(shippingContact.phoneNumber?.trim())) {
        errors.push(
          new ApplePayError(
            'shippingContactInvalid',
            'emailAddress',
            'Invalid shipping contact email address.'
          )
        );
      }

      if (!billingContact) {
        errors.push(
          new ApplePayError(
            'billingContactInvalid',
            'postalCode',
            'Missing billing contact information.'
          )
        );
      }

      if (errors.length > 0) {
        session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors
        });
        // Do not reject the promise here to allow the customer to retry the payment.
        return;
      }

      const requiredShippingContact = shippingContact!;
      const requiredBillingContact = billingContact!;

      const shippingEmail = requiredShippingContact.emailAddress!.trim();
      const phoneNumber = requiredShippingContact.phoneNumber!.trim();

      let shippingInfo: OrderShippingInfoModel;
      if (cart.type === 'gift-card') {
        shippingInfo = new OrderDigitalShippingInfoModel({
          email: shippingEmail,
          phoneNumber
        });
      } else {
        const { countryCode } = requiredShippingContact;

        const shippingAddress = this.transformShippingContact(
          requiredShippingContact
        ) as IAddress;

        if (!shippingAddress.firstName?.trim()) {
          errors.push(
            new ApplePayError(
              'shippingContactInvalid',
              'name',
              'Missing first name in shipping contact.'
            )
          );
        }

        if (!shippingAddress.lastName?.trim()) {
          errors.push(
            new ApplePayError(
              'shippingContactInvalid',
              'name',
              'Missing last name in shipping contact.'
            )
          );
        }

        if (errors.length > 0) {
          session.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
            errors
          });
          // Do not reject the promise here to allow the customer to retry the payment.
          return;
        }

        if (
          !isFullNameDOMSCompatible([
            shippingAddress.firstName,
            shippingAddress.lastName
          ])
        ) {
          errors.push(
            new ApplePayError(
              'shippingContactInvalid',
              'name',
              msg(checkout_shipping_errors_nameTooLong)
            )
          );
        }

        if (isNullOrEmpty(shippingEmail) || !isValidEmail(shippingEmail)) {
          errors.push(
            new ApplePayError(
              'shippingContactInvalid',
              'emailAddress',
              msgf(forms_error_validEmail, {
                name: msg(forms_field_email).toLowerCase()
              })
            )
          );
        }

        const { safeValidatePhoneNumber } = await phoneUtils;

        if (
          isNullOrEmpty(phoneNumber) ||
          isNullOrEmpty(countryCode) ||
          !safeValidatePhoneNumber(phoneNumber, countryCode as Country)
        ) {
          errors.push(
            new ApplePayError(
              'shippingContactInvalid',
              'phoneNumber',
              msgf(forms_error_validPhoneNumber, {
                name: msg(forms_field_phone).toLowerCase()
              })
            )
          );
        }

        /**
         * We need to sync the cart's shipping address with the
         * full address info returned upon authorization.
         *
         * TODO: find a synchronous way to do this since this runs
         * revalidation again, which is unnecessary.
         */
        await cart.setShipToAddress(shippingAddress);

        shippingInfo = new OrderPhysicalShippingInfoModel({
          email: shippingEmail,
          phoneNumber,
          shippingMethod: cart.selectedShippingMethod!.uid,
          address: shippingAddress,
          shouldSaveAddress: false
        });
      }

      const billingAddress = this.transformShippingContact(
        requiredBillingContact
      ) as IAddress;

      if (!billingAddress.firstName?.trim()) {
        errors.push(
          new ApplePayError(
            'billingContactInvalid',
            'name',
            'Missing first name in billing contact.'
          )
        );
      }

      if (!billingAddress.lastName?.trim()) {
        errors.push(
          new ApplePayError(
            'billingContactInvalid',
            'name',
            'Missing last name in billing contact.'
          )
        );
      }

      if (errors.length > 0) {
        session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors
        });
        // Do not reject the promise here to allow the customer to retry the payment.
        return;
      }

      const expressPayment: IExpressPayment<ApplePayPaymentBundle> = {
        shippingInfo,
        billingAddress,
        data: token
      };

      try {
        const order = await onPaymentAuthorized(expressPayment);
        // resolve the promise first so that any dependent actions have a
        // chance to run even if `completePayment` fails after for some reason.
        resolve(order);

        session.completePayment({
          status: ApplePaySession.STATUS_SUCCESS
          // TODO: use the placed order to populate the order details
          // orderDetails: {}
        });
      } catch (cause) {
        LoggerService.error(
          new Error('Payment authorization failed.', { cause })
        );

        session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('unknown')]
        });
        // Do not reject the promise here to allow the customer to retry the payment.
      }
    };

    let inPaymentAuthorization = false;
    session.onpaymentauthorized = (event) => {
      const { payment } = event;
      inPaymentAuthorization = true;

      void invokeImmediately(async () => {
        try {
          // if this takes over 30 seconds, the session is automatically canceled.
          // inPaymentAuthorization is used to throw the correct interruption error type.
          await paymentAuthorizationMainHandler(payment);
        } finally {
          inPaymentAuthorization = false;
        }
      });
    };
    // #endregion

    // #region Payment Cancellation Handling
    session.oncancel = () => {
      if (inPaymentAuthorization) {
        reject(new PaymentTimeoutError('Payment timed out.'));
      } else {
        reject(new PaymentCanceledError('Payment was canceled.'));
      }
    };
    // #endregion

    // Present the payment sheet and initiate the merchant validation process.
    session.begin();

    return promise;
  }
}
